import { useMemo } from 'react';
import { useAssistantTool } from '@assistant-ui/react';
import {
  fetchContactDonationSummary,
  searchContacts,
} from '../../../utils/RocksetQueries/SearchQueries';
import { P } from '../../../utils/permissions';
import {
  DASHBOARD_ASSISTANT_RUNTIME_MODE,
  MAX_SEARCH_RESULTS_PER_CATEGORY,
} from '../constants';
import { objectToolParameters } from '../lib/assistantToolSchemas';
import { hasPermission, restrictedResult } from '../lib/permissions';
import {
  dedupeBy,
  getContactMatchScore,
  getMerchantNames,
  limitItems,
} from '../lib/helpers';
import {
  createFrontendToolActionMessage,
  createFrontendToolErrorMessage,
} from '../lib/toolActionMessages';
import {
  serializeDonationSummary,
  serializeSearchContact,
} from '../lib/serializers';
import { buildSearchResult, createCompactSearchToolResult } from '../lib/searchResultBuilders';

export function useGetContactDonationSummaryAction({
  activeSessionId,
  dashboardTimezoneOffset,
  dispatchActionMessage,
  normalizedDashboardTimezone,
  organizationName,
}) {
  const parameters = useMemo(
    () =>
      objectToolParameters({
        properties: {
          contactId: {
            type: 'string',
            description:
              'The CharityStack contact ID. Prefer this when a prior tool result already returned a contactId.',
          },
          query: {
            type: 'string',
            description: 'Contact name or email to resolve when contactId is not known.',
          },
        },
      }),
    [],
  );

  const tool = useMemo(
    () => ({
      toolName: 'get_contact_donation_summary',
      type: 'frontend',
      description:
        'Get exact donation lifecycle facts for a contact, including first donation date, last donation date, donation count, and lifetime donation totals. Use this for questions about first donation, last donation, lifetime giving, total donations, or donation history facts. If you only have a name or email, pass it as query so the tool can resolve the contact first.',
      parameters,
      execute: async ({ contactId, query }) => {
        const merchants = await getMerchantNames(organizationName);
        const requestedContactId = contactId?.trim?.();
        const keyword = query?.trim?.();
        let resolvedContactId = requestedContactId;
        let resolvedContact = null;

        if (!hasPermission(P.VIEW_PAYMENTS)) {
          const result = restrictedResult('payments');
          dispatchActionMessage(createFrontendToolErrorMessage({
            message: result.message,
            status: result.status,
            toolArgs: { contactId: requestedContactId, query: keyword },
            toolName: 'get_contact_donation_summary',
            toolResult: result,
          }));
          return result;
        }

        if (merchants.length === 0) {
          const result = {
            runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
            status: 'error',
            message: 'No active organization name is available for donation summary lookup.',
          };
          dispatchActionMessage(createFrontendToolErrorMessage({
            message: result.message,
            status: result.status,
            toolArgs: { contactId: requestedContactId, query: keyword },
            toolName: 'get_contact_donation_summary',
            toolResult: result,
          }));
          return result;
        }

        if (!resolvedContactId) {
          if (!keyword || keyword.length < 2) {
            const result = {
              runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
              status: 'error',
              message: 'A contact ID or contact search query of at least 2 characters is required.',
            };
            dispatchActionMessage(createFrontendToolErrorMessage({
              message: result.message,
              status: result.status,
              toolArgs: { contactId: requestedContactId, query: keyword },
              toolName: 'get_contact_donation_summary',
              toolResult: result,
            }));
            return result;
          }

          if (!hasPermission(P.VIEW_CONTACTS)) {
            const result = restrictedResult('contacts');
            dispatchActionMessage(createFrontendToolErrorMessage({
              message: result.message,
              status: result.status,
              toolArgs: { contactId: requestedContactId, query: keyword },
              toolName: 'get_contact_donation_summary',
              toolResult: result,
            }));
            return result;
          }

          const contactsRaw = await searchContacts(merchants, keyword);
          const contacts = limitItems(
            dedupeBy(contactsRaw, (contact) => contact.contactID),
            MAX_SEARCH_RESULTS_PER_CATEGORY,
          );
          const rankedContacts = contacts
            .map((contact) => ({ contact, score: getContactMatchScore(contact, keyword) }))
            .filter(({ score }) => score > 0)
            .sort((left, right) => right.score - left.score);
          const clearMatch =
            rankedContacts.length === 1 ||
            (rankedContacts.length > 1 && rankedContacts[0].score >= 95 && rankedContacts[1].score < 95)
              ? rankedContacts[0].contact
              : contacts.length === 1
                ? contacts[0]
                : null;

          if (!clearMatch?.contactID) {
            const searchResult = buildSearchResult({ contacts, organizationName, query: keyword });
            const displayPayload = {
              renderer: 'dashboard_search_results',
              summary: `Searched contacts for "${keyword}"`,
              data: searchResult,
            };

            const compactResult = createCompactSearchToolResult(searchResult);
            const status = contacts.length > 0 ? 'needs_disambiguation' : 'not_found';
            dispatchActionMessage(createFrontendToolActionMessage({
              actionType: 'search',
              content: displayPayload.summary,
              displayPayload,
              status,
              toolArgs: { contactId: requestedContactId, query: keyword },
              toolName: 'get_contact_donation_summary',
              toolResult: {
                ...compactResult,
                status,
              },
            }));

            return {
              ...compactResult,
              status,
              guidance:
                contacts.length > 0
                  ? 'Ask the user which contact to inspect. Do not infer donation summary facts until one contact is selected.'
                  : 'Tell the user no matching contact was found in the active organization.',
            };
          }

          resolvedContact = clearMatch;
          resolvedContactId = clearMatch.contactID;
        }

        const summaries = await fetchContactDonationSummary(merchants, [resolvedContactId]);
        const summary = serializeDonationSummary(summaries?.[0], normalizedDashboardTimezone);
        const contactLabel =
          resolvedContact?.name || resolvedContact?.email || resolvedContactId || 'contact';
        const hasDonations = Boolean(summary && Number(summary.donationCount || 0) > 0);
        const result = {
          runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
          status: hasDonations ? 'ok' : 'no_donations',
          organizationName,
          timezone: normalizedDashboardTimezone,
          timezoneOffset: dashboardTimezoneOffset,
          contact: resolvedContact ? serializeSearchContact(resolvedContact) : { contactId: resolvedContactId },
          donationSummary: summary,
          guidance:
            'Use only these computed donationSummary fields for first donation, last donation, donation count, and lifetime giving facts. Prefer firstDonationAtFormatted and lastDonationAtFormatted when mentioning dates. Do not infer these facts from recent payment search results.',
        };

        dispatchActionMessage(createFrontendToolActionMessage({
          actionType: 'search',
          content: `Checked donation summary for ${contactLabel}`,
          status: result.status,
          toolArgs: { contactId: requestedContactId, query: keyword },
          toolName: 'get_contact_donation_summary',
          toolResult: result,
        }));

        return result;
      },
    }),
    [
      activeSessionId,
      dashboardTimezoneOffset,
      dispatchActionMessage,
      normalizedDashboardTimezone,
      organizationName,
      parameters,
    ],
  );

  useAssistantTool(tool);
}
